import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";

/**
 * V231 — Dedicated 404 page.
 *
 * Previously `path="*"` fell through to `<App />` which returned
 * HTTP 200 + the homepage shell for every unknown URL. Google
 * interpreted that as "Falošné 404" / Soft 404 across hundreds of
 * invented URLs (`/ar`, `/en`, `/blog?q={search_term_string}` ...).
 *
 * This component:
 *   • Injects `<meta name="robots" content="noindex,follow">` so
 *     Google de-indexes the URL on the next crawl.
 *   • Injects `<meta name="prerender-status-code" content="404">`
 *     and `<title>` prefix "404 — " so prerender services and SEO
 *     tooling see a real 404 signal even when the HTTP code is 200
 *     (CRA SPAs can't return 4xx without a reverse proxy).
 *   • Keeps the user in-funnel with a visible CTA back to /scan.
 */
export default function NotFoundPage() {
  const location = useLocation();

  useEffect(() => {
    // Mark THIS render as a 404 for crawlers.
    const prevTitle = document.title;
    document.title = "404 — Stránka nenájdená · TopRankPulse";

    // Override the default <meta name="robots"> already in index.html
    // (which says index,follow). Store the previous value so we restore
    // it on unmount — otherwise SPA navigation away from 404 would
    // keep noindex on the next page.
    const robotsEl = document.querySelector('meta[name="robots"]');
    const prevRobots = robotsEl?.getAttribute("content") ?? null;
    if (robotsEl) {
      robotsEl.setAttribute("content", "noindex,follow");
    } else {
      const m = document.createElement("meta");
      m.name = "robots";
      m.content = "noindex,follow";
      m.setAttribute("data-404-added", "true");
      document.head.appendChild(m);
    }

    // Prerender / Rendertron-friendly 404 signal.
    const prerender = document.createElement("meta");
    prerender.name = "prerender-status-code";
    prerender.content = "404";
    prerender.setAttribute("data-404", "true");
    document.head.appendChild(prerender);

    return () => {
      // Restore previous state so SPA navigation to a real page is indexable.
      const el = document.querySelector('meta[name="robots"]');
      if (el?.getAttribute("data-404-added") === "true") {
        el.remove();
      } else if (el && prevRobots !== null) {
        el.setAttribute("content", prevRobots);
      }
      document.head
        .querySelectorAll('meta[data-404="true"]')
        .forEach((n) => n.remove());
      document.title = prevTitle;
    };
  }, []);

  return (
    <main
      className="min-h-screen bg-[#0A0F2E] text-white flex items-center justify-center px-6"
      data-testid="not-found-page"
    >
      <div className="max-w-xl text-center">
        <p className="text-xs tracking-[0.3em] text-amber-400 font-semibold mb-4">
          404 · STRÁNKA NENÁJDENÁ
        </p>
        <h1 className="text-4xl sm:text-5xl font-bold mb-5 tracking-tight">
          Táto stránka neexistuje.
        </h1>
        <p className="text-slate-300 text-base sm:text-lg mb-8 leading-relaxed">
          URL <code className="bg-white/10 px-1.5 py-0.5 rounded text-amber-300">
            {location.pathname}
          </code>{" "}
          sme nenašli. Mohla byť presunutá alebo zrušená.
        </p>
        <div className="flex flex-wrap gap-3 justify-center">
          <Link
            to="/"
            data-testid="back-home-link"
            className="bg-amber-400 hover:bg-amber-300 text-black font-bold px-6 py-3 rounded-lg transition"
          >
            Domov
          </Link>
          <Link
            to="/scan"
            data-testid="start-scan-link"
            className="border border-white/20 hover:border-white/40 text-white font-semibold px-6 py-3 rounded-lg transition"
          >
            Spustiť AI SEO scan →
          </Link>
        </div>
        <p className="mt-10 text-xs text-slate-500">
          Hľadáš niečo konkrétne?{" "}
          <Link to="/blog" className="underline hover:text-amber-300">
            Blog
          </Link>{" "}
          ·{" "}
          <Link to="/tools" className="underline hover:text-amber-300">
            Nástroje
          </Link>{" "}
          ·{" "}
          <Link to="/pricing" className="underline hover:text-amber-300">
            Cenník
          </Link>
        </p>
      </div>
    </main>
  );
}
